import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import api from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const storeToken = (token) => localStorage.setItem("ks_token", token);
    const clearToken = () => localStorage.removeItem("ks_token");

    const loadUser = useCallback(async () => {
        const token = localStorage.getItem("ks_token");
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const { user: u } = await api.me();
            setUser(u);
        } catch {
            clearToken();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (credentials) => {
        const { user: u, token } = await api.login(credentials);
        storeToken(token);
        setUser(u);
        return u;
    };

    const register = async (data) => {
        const { user: u, token } = await api.register(data);
        storeToken(token);
        setUser(u);
        return u;
    };

    const logout = async () => {
        try {
            await api.logout();
        } catch {}
        clearToken();
        localStorage.removeItem("ks_admin_token");
        setUser(null);
    };

    const updateUser = (updates) =>
        setUser((prev) => ({ ...prev, ...updates }));

    // ---- Admin impersonation ----
    const isImpersonating = !!localStorage.getItem("ks_admin_token");

    const impersonate = async (clientId) => {
        const { token } = await api.impersonateClient(clientId);
        // Keep the admin token so we can switch back
        localStorage.setItem(
            "ks_admin_token",
            localStorage.getItem("ks_token"),
        );
        storeToken(token);
        // Full reload so every component refetches as the client
        window.location.href = "/dashboard";
    };

    const stopImpersonating = async () => {
        try {
            await api.logout(); // revoke the impersonation token
        } catch {}
        const adminToken = localStorage.getItem("ks_admin_token");
        localStorage.removeItem("ks_admin_token");
        if (adminToken) {
            storeToken(adminToken);
            window.location.href = "/admin";
        } else {
            clearToken();
            window.location.href = "/client-portal";
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                register,
                logout,
                updateUser,
                impersonate,
                isImpersonating,
                stopImpersonating,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export default AuthContext;
